/**
 * A ~40 line DOM helper instead of a framework.
 *
 * NabuSu has ten screens, heavy custom animation and a hard requirement to boot
 * instantly from a cold cache on a cheap phone. A framework would cost more
 * bytes than the entire rest of the app.
 */

type Child = Node | string | number | false | null | undefined
type Attrs = Record<string, unknown>

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag)
  for (const [key, value] of Object.entries(attrs)) {
    if (value === false || value == null) continue
    if (key === 'class') el.className = String(value)
    else if (key === 'style' && typeof value === 'object')
      Object.assign(el.style, value as object)
    else if (key.startsWith('on') && typeof value === 'function')
      el.addEventListener(key.slice(2).toLowerCase(), value as EventListener)
    else if (key === 'html') el.innerHTML = String(value)
    else if (key in el && key !== 'list') (el as any)[key] = value
    else el.setAttribute(key, String(value))
  }
  append(el, children)
  return el
}

export function append(parent: Node, children: Child[]): void {
  for (const child of children) {
    if (child === false || child == null || child === '') continue
    parent.appendChild(
      typeof child === 'string' || typeof child === 'number'
        ? document.createTextNode(String(child))
        : child,
    )
  }
}

export function frag(...children: Child[]): DocumentFragment {
  const f = document.createDocumentFragment()
  append(f, children)
  return f
}

export function clear(el: Element): void {
  while (el.firstChild) el.removeChild(el.firstChild)
}

export function $(selector: string, root: ParentNode = document): HTMLElement | null {
  return root.querySelector(selector)
}

/** Wait, but cancellable — every timed sequence in this app must be abortable. */
export function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException('aborted', 'AbortError'))
    const id = setTimeout(resolve, ms)
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(id)
        reject(new DOMException('aborted', 'AbortError'))
      },
      { once: true },
    )
  })
}

export function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError'
}
